import { Card, Button, Descriptions, Modal, Input, message, Space, Tag, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useState } from 'react';
import type { TenantOrder } from '@/mocks/orders';
import { formatDate, formatVND } from '@/utils/format';
import { useRevealOrderSensitive } from '@/hooks/useOrderMutations';
import { describeFulfillment } from '@/pages/POS/components/Fulfillment/fulfillmentChoice';
import { SHIPPING_ORDER_STATE_LABELS } from '@/constants/shippingOrderStates';
import { getDeliveryOverdueStatus, getDeliveryWarningBadge } from '../utils/deliveryOverdue';
import {
  resolveShippingFeeDisplay,
  SHIPPING_FEE_ESTIMATE_HINT,
  SHIPPING_FEE_ESTIMATE_LABEL,
} from '../utils/shippingFeeDisplay';

interface ShippingCardProps {
  order: TenantOrder;
  onEdit?: () => void;
  /**
   * Bỏ khối "Thông tin giao vận" nối đuôi bên dưới (TCR-1095): pop-up xem nhanh đã có card
   * "Vận chuyển" riêng ngay dưới card này, để cả hai thì cùng một thông tin hiện hai lần.
   */
  hideCarrierInfo?: boolean;
  /**
   * Có mặt (khác undefined) → hiện nút "Sửa" riêng cạnh "Ghi chú vận chuyển"
   * trong khối "Thông tin giao vận" (TCR-1221). Field này KHÔNG đi qua
   * `onEdit` ở trên — đó là sửa "Thông tin nhận hàng" (địa chỉ), khác domain.
   */
  onEditShippingNote?: () => void;
}

export function ShippingCard({ order, onEdit, hideCarrierInfo, onEditShippingNote }: ShippingCardProps) {
  const { shipping } = order;
  const addr = shipping?.address;
  const partner = shipping?.shipping_partner;
  const pickupLocation = shipping?.pickup_location;
  const deliveryOverdue = getDeliveryOverdueStatus(order);
  const deliveryWarningBadge = getDeliveryWarningBadge(deliveryOverdue);
  const labelEntries = Object.entries(shipping?.labels ?? {});
  /*
   * TCR-2605: hãng chưa báo phí thật thì đơn chỉ có báo giá (`shipping_fee_estimate`).
   * Khối "Thông tin giao vận" phải hiện được cả con số đó — nếu không, người xem
   * đơn thấy `0 ₫`/`---` và tưởng đơn miễn phí vận chuyển.
   */
  const shippingFeeDisplay = resolveShippingFeeDisplay({
    actual: shipping?.shipping_cost,
    estimate: order.shipping_fee_estimate,
  });
  const hasCarrierInfo = Boolean(
    partner?.name ||
    partner?.order_id ||
    partner?.tracking_url ||
    shipping?.state ||
    shipping?.shipping_cost ||
    shipping?.pickup_location_id ||
    shipping?.shipping_order?.order_number ||
    order.shipping_fee_estimate ||
    labelEntries.length > 0,
  );
  
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isCccdVisible, setIsCccdVisible] = useState(false);

  const revealMutation = useRevealOrderSensitive();
  const [decryptedCccd, setDecryptedCccd] = useState<string | null>(null);

  const handleViewCccd = () => {
    setIsVerifyModalOpen(true);
  };

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      message.error('Vui lòng nhập mật khẩu');
      return;
    }
    try {
      const res = await revealMutation.mutateAsync({ id: order._id, password });
      setIsVerifyModalOpen(false);
      setDecryptedCccd(res.citizen_id || addr?.citizen_id || '');
      setIsCccdVisible(true);
      setPassword('');
      message.success('Giải mã thành công');
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Mật khẩu không đúng hoặc có lỗi xảy ra');
    }
  };

  const renderCccd = () => {
    if (!addr?.citizen_id) return '---';
    if (isCccdVisible && decryptedCccd) {
      return decryptedCccd;
    }
    
    // Nếu là ciphertext (thường bắt đầu bằng v1:) thì mask hết, nếu là plaintext 12 số thì mask giữa
    if (addr.citizen_id.startsWith('v1:')) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>********</span>
          <EyeOutlined style={{ cursor: 'pointer', color: '#0958d9' }} onClick={handleViewCccd} />
        </span>
      );
    }
    
    const cccd = addr.citizen_id;
    const masked = cccd.length >= 10 ? `${cccd.slice(0, 3)}***${cccd.slice(-4)}` : '***';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span>{masked}</span>
        <EyeOutlined style={{ cursor: 'pointer', color: '#0958d9' }} onClick={handleViewCccd} />
      </span>
    );
  };

  if (!addr) {
    return (
      <Card
        className="order-shipping-card order-detail-card"
        title="Thông tin nhận hàng"
        size="small"
        extra={
          onEdit && <Button size="small" onClick={onEdit}>
            Sửa
          </Button>
        }
      >
        <div style={{ color: '#6b6b6b', textAlign: 'center', padding: '12px 0' }}>
          Chưa có thông tin nhận hàng
        </div>
      </Card>
    );
  }

  const expectedDate = deliveryOverdue.deadline
    ? formatDate(deliveryOverdue.deadline.toISOString())
    : '---';

  return (
    <>
      <Card
        className="order-shipping-card order-detail-card"
        title="Thông tin nhận hàng"
        size="small"
        extra={
          onEdit && <Button size="small" onClick={onEdit}>
            Sửa
          </Button>
        }
      >
        <Descriptions column={1} size="small" bordered={false} labelStyle={{ color: '#666', width: 140 }}>
          <Descriptions.Item label="Họ tên">
            <strong>{addr.name}</strong>
          </Descriptions.Item>
          <Descriptions.Item label="Số điện thoại">
            {addr.phone}
          </Descriptions.Item>
          <Descriptions.Item label="Số CCCD">
            {renderCccd()}
          </Descriptions.Item>
          <Descriptions.Item label="Thời gian giao hàng">
            <Space size={8} wrap>
              <span>{shipping?.estimated_days ? `Sau ${shipping.estimated_days} ngày` : '---'}</span>
              {deliveryWarningBadge && (
                <Tag color={deliveryWarningBadge.color} style={{ marginInlineEnd: 0 }}>
                  {deliveryWarningBadge.label}
                </Tag>
              )}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Ngày giao dự kiến">
            {expectedDate}
          </Descriptions.Item>
          {/*
            Dùng CHUNG hàm mô tả với màn POS, không tự switch lại ở đây.
            `self` và `carrier` cùng lưu `method: 'delivery'` nên nhìn một field
            là dồn hai chế độ vào một nhãn "Giao hàng" — người xem đơn không biết
            ai đang cầm hàng. Câu chữ cũng phải khớp POS để khỏi phải dịch ngầm
            giữa hai màn.
          */}
          <Descriptions.Item label="Phương thức nhận">
            {describeFulfillment({
              receive_method: shipping?.method,
              shipping_mode: shipping?.shipping_mode,
            })}
          </Descriptions.Item>
          {addr.address_name?.trim() && (
            <Descriptions.Item label="Tên địa chỉ giao hàng">{addr.address_name.trim()}</Descriptions.Item>
          )}
          <Descriptions.Item label="Địa chỉ nhận hàng">
            {[addr.address, addr.ward_name, addr.city_name].filter(Boolean).join(', ')}
          </Descriptions.Item>
          {addr.note?.trim() && (
            <Descriptions.Item label="Ghi chú giao hàng">
              <span style={{ whiteSpace: 'pre-line' }}>{addr.note.trim()}</span>
            </Descriptions.Item>
          )}
        </Descriptions>
        {hasCarrierInfo && !hideCarrierInfo && (
          <>
            <div style={{ borderTop: '1px solid #f0f0f0', margin: '12px 0' }} />
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Thông tin giao vận</div>
            <Descriptions column={1} size="small" bordered={false} labelStyle={{ color: '#666', width: 140 }}>
              <Descriptions.Item label="Đơn vị vận chuyển">
                {partner?.name ? <Tag color="blue">{partner.name}</Tag> : '---'}
              </Descriptions.Item>
              {shipping?.shipping_order?.order_number && (
                <Descriptions.Item label="Mã đơn giao vận (nội bộ)">
                  {shipping.shipping_order.order_number}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Mã vận đơn">
                {partner?.order_id || '---'}
              </Descriptions.Item>
              <Descriptions.Item label="Ghi chú vận chuyển">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: order.shipping_note ? undefined : '#999', fontStyle: order.shipping_note ? 'normal' : 'italic' }}>
                    {order.shipping_note || 'Chưa có ghi chú'}
                  </span>
                  {onEditShippingNote && (
                    <Button size="small" type="link" style={{ padding: 0, height: 'auto' }} aria-label="Sửa ghi chú vận chuyển" onClick={onEditShippingNote}>
                      Sửa
                    </Button>
                  )}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Trạng thái giao vận">
                {shipping?.state ? (
                  <Tag>{SHIPPING_ORDER_STATE_LABELS[shipping.state] ?? shipping.state}</Tag>
                ) : (
                  '---'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Phí vận chuyển">
                {shippingFeeDisplay.amount != null ? (
                  <Space size={8} wrap>
                    <span>{formatVND(shippingFeeDisplay.amount)}</span>
                    {shippingFeeDisplay.isEstimate && (
                      <Tooltip title={SHIPPING_FEE_ESTIMATE_HINT}>
                        <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                          {SHIPPING_FEE_ESTIMATE_LABEL}
                        </Tag>
                      </Tooltip>
                    )}
                  </Space>
                ) : (
                  '---'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Nơi lấy hàng">
                {/* Ô giá trị của antd Descriptions là flex ngang: để tên (text) và
                    địa chỉ (div) đứng rời thì chúng thành 2 cột hẹp (TCR-2453).
                    Gói chung một khối để tên ở trên, địa chỉ ở dưới. */}
                <div>
                  <div>{pickupLocation?.name || shipping?.pickup_location_id || '---'}</div>
                  {pickupLocation?.address && (
                    <div style={{ color: '#666', marginTop: 2 }}>{pickupLocation.address}</div>
                  )}
                </div>
              </Descriptions.Item>
              {partner?.tracking_url && (
                <Descriptions.Item label="Tracking URL">
                  <a href={partner.tracking_url} target="_blank" rel="noreferrer">
                    Mở tracking
                  </a>
                </Descriptions.Item>
              )}
              {labelEntries.length > 0 && (
                <Descriptions.Item label="Label cache">
                  <Space size={[4, 4]} wrap>
                    {/* Hiện TÊN template chứ không hiện khoá: nhãn hãng ngoài
                        sàn nay dùng chung khoá `service_label`, in khoá ra thì
                        mọi hãng trông giống hệt nhau. `template_name` nói rõ
                        "Best Express waybill" hay "GHTK PDF label". */}
                    {labelEntries.map(([templateCode, label]) => (
                      <Tag key={templateCode} color={label.status === 'generated' ? 'green' : label.status === 'failed' ? 'red' : 'default'}>
                        {label.template_name || templateCode}: {label.status || 'unknown'}
                      </Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </>
        )}
      </Card>

      <Modal
        title={<span style={{ fontSize: 16, fontWeight: 600 }}>Xác nhận mật khẩu</span>}
        open={isVerifyModalOpen}
        onOk={handleVerifyPassword}
        onCancel={() => {
          setIsVerifyModalOpen(false);
          setPassword('');
        }}
        okText="Xác nhận"
        cancelText="Hủy"
        width="min(400px, calc(100vw - 32px))"
        confirmLoading={revealMutation.isPending}
        okButtonProps={{
          style: {
            backgroundColor: '#A81C3F',
            borderColor: '#A81C3F',
            color: '#fff',
            borderRadius: 6,
          }
        }}
      >
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 8, fontSize: 14 }}>
            Vui lòng nhập mật khẩu để xem thông tin nhạy cảm:
          </div>
          <Input.Password
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mật khẩu"
            onPressEnter={handleVerifyPassword}
          />
        </div>
      </Modal>
    </>
  );
}
